/**
 * CastPanel - the roster, read as people.
 *
 * Everyone else first (each with the line that describes them), then the
 * party. A name opens that person in the target panel - sheets are one
 * press away. Whoever directs (`canDirect`) also writes each description,
 * sets a disposition (which only they are shown), and adds a person by
 * typing one line.
 *
 * @param {object} props
 * @param {{ party: object[], others: object[], setPersona: Function, addPerson: Function, open: Function }} props.cast
 * @param {boolean} props.canDirect
 */
import { useState } from 'react';
import common from '../../styles/common.module.css';
import styles from '../../styles/Story.module.css';
import { DISPOSITIONS, personaOf } from '../../engine/narrative/cast';

/** The description, written in place: committed when the field is left. */
function DescriptionField({ person, onCommit }) {
  const stored = personaOf(person).description ?? '';
  const [draft, setDraft] = useState(stored);
  // Follow the stored line when it changes under the field (another screen, an import).
  const [seen, setSeen] = useState(stored);
  if (seen !== stored) {
    setSeen(stored);
    setDraft(stored);
  }
  return (
    <input
      className={styles.input}
      aria-label={`Describe ${person.name}`}
      placeholder="Who they are, in a line"
      value={draft}
      maxLength={200}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => draft !== stored && onCommit(draft)}
    />
  );
}

function DispositionField({ person, onCommit }) {
  return (
    <select
      className={styles.input}
      aria-label={`Disposition of ${person.name}`}
      value={personaOf(person).disposition ?? ''}
      onChange={(e) => onCommit(e.target.value)}
    >
      <option value="">No disposition</option>
      {DISPOSITIONS.map((d) => (
        <option key={d} value={d}>
          {d}
        </option>
      ))}
    </select>
  );
}

function Person({ person, cast, canDirect, described }) {
  const persona = personaOf(person);
  return (
    <li className={styles.item} data-testid="cast-person">
      <button type="button" className={styles.person} onClick={() => cast.open(person.id)}>
        {person.name}
      </button>
      {described && !canDirect && persona.description && <p className={styles.body}>{persona.description}</p>}
      {described && canDirect && (
        <div className={styles.tools}>
          <DescriptionField person={person} onCommit={(description) => cast.setPersona(person.id, { description })} />
          <DispositionField person={person} onCommit={(disposition) => cast.setPersona(person.id, { disposition })} />
        </div>
      )}
    </li>
  );
}

function AddPerson({ onAdd }) {
  const [line, setLine] = useState('');
  const submit = (e) => {
    e.preventDefault();
    if (!line.trim()) return;
    onAdd(line);
    setLine('');
  };
  return (
    <form className={styles.form} onSubmit={submit}>
      <label className={styles.field}>
        A person in one line
        <input
          className={styles.input}
          data-testid="cast-line"
          placeholder="Name - who they are"
          value={line}
          maxLength={260}
          onChange={(e) => setLine(e.target.value)}
        />
      </label>
      <button type="submit" className={common.btnSmall} data-testid="cast-add" disabled={!line.trim()}>
        Add
      </button>
    </form>
  );
}

function People({ caption, people, cast, canDirect, described }) {
  if (people.length === 0) return null;
  return (
    <>
      <h3 className={styles.caption}>{caption}</h3>
      <ul className={styles.list}>
        {people.map((person) => (
          <Person key={person.id} person={person} cast={cast} canDirect={canDirect} described={described} />
        ))}
      </ul>
    </>
  );
}

export default function CastPanel({ cast, canDirect }) {
  const nobody = cast.party.length === 0 && cast.others.length === 0;
  return (
    <section className={styles.section} aria-label="The cast" data-testid="cast-panel">
      <People caption="The cast" people={cast.others} cast={cast} canDirect={canDirect} described />
      <People caption="The party" people={cast.party} cast={cast} canDirect={canDirect} described={false} />
      {nobody && (
        <>
          <h3 className={styles.caption}>The cast</h3>
          <p className={styles.empty}>Nobody is here yet.</p>
        </>
      )}
      {canDirect && <AddPerson onAdd={cast.addPerson} />}
    </section>
  );
}
